import React, { createContext, useContext, useReducer, useEffect } from 'react';

const LanguageContext = createContext();

// Language reducer
const languageReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LANGUAGE':
      return { ...state, language: action.payload };
    case 'TOGGLE_LANGUAGE':
      return { ...state, language: state.language === 'en' ? 'ar' : 'en' };
    case 'SET_DIRECTION':
      return { ...state, direction: action.payload };
    default:
      return state;
  }
};

const initialState = {
  language: 'en',
  direction: 'ltr'
};

// Translation data
const translations = {
  en: {
    // Authentication
    login: 'Login',
    logout: 'Logout',
    studentId: 'Student ID',
    fullName: 'Full Name',
    loginButton: 'Login',
    loginError: 'Login failed. Please try again.',
    accessDenied: 'Access denied. Please contact admin.',
    pendingApproval: 'Your account is pending approval.',
    
    // Navigation
    library: 'Library',
    admin: 'Admin',
    dashboard: 'Dashboard',
    
    // Library
    searchPlaceholder: 'Search lessons...',
    filterBySubject: 'Filter by Subject',
    allSubjects: 'All Subjects',
    viewList: 'List View',
    viewGrid: 'Grid View',
    noResults: 'No results found',
    loadingContent: 'Loading content...',
    
    // File types
    pdf: 'PDF',
    video: 'Video',
    audio: 'Audio',
    
    // Admin
    pendingUsers: 'Pending Users',
    approvedUsers: 'Approved Users',
    approve: 'Approve',
    reject: 'Reject',
    batchSettings: 'Batch Settings',
    currentBatch: 'Current Batch',
    'System Configuration': 'System Configuration',
    'Google Sheet ID': 'Google Sheet ID',
    'Batch Configuration': 'Batch Configuration',
    'Update Batch': 'Update Batch',
    'Updating...': 'Updating...',
    'Batch updated successfully!': 'Batch updated successfully!',
    'Batch': 'Batch',
    'Changing the batch will affect all users.': 'Changing the batch will affect all users.',
    'The ID of the Google Sheet containing batch data.': 'The ID of the Google Sheet containing batch data.',
    
    // Common
    loading: 'Loading...',
    error: 'Error',
    retry: 'Retry',
    close: 'Close',
    save: 'Save',
    cancel: 'Cancel',
    
    // Footer
    poweredBy: 'Powered by EduTransform',
    version: 'Version 2.0'
  },
  ar: {
    // Authentication
    login: 'تسجيل الدخول',
    logout: 'تسجيل الخروج',
    studentId: 'رقم الطالب',
    fullName: 'الاسم الكامل',
    loginButton: 'دخول',
    loginError: 'فشل تسجيل الدخول. حاول مرة أخرى.',
    accessDenied: 'تم رفض الوصول. يرجى الاتصال بالإدارة.',
    pendingApproval: 'حسابك في انتظار الموافقة.',
    
    // Navigation
    library: 'المكتبة',
    admin: 'الإدارة',
    dashboard: 'لوحة التحكم',
    
    // Library
    searchPlaceholder: 'ابحث عن الدروس...',
    filterBySubject: 'فلترة حسب المادة',
    allSubjects: 'جميع المواد',
    viewList: 'عرض القائمة',
    viewGrid: 'عرض الشبكة',
    noResults: 'لا توجد نتائج',
    loadingContent: 'جاري تحميل المحتوى...',
    
    // File types
    pdf: 'ملف PDF',
    video: 'فيديو',
    audio: 'صوت',
    
    // Admin
    pendingUsers: 'المستخدمون المعلقون',
    approvedUsers: 'المستخدمون المعتمدون',
    approve: 'موافقة',
    reject: 'رفض',
    batchSettings: 'إعدادات الدفعة',
    currentBatch: 'الدفعة الحالية',
    'System Configuration': 'إعدادات النظام',
    'Google Sheet ID': 'معرف جدول بيانات جوجل',
    'Batch Configuration': 'إعدادات الدفعة',
    'Update Batch': 'تحديث الدفعة',
    'Updating...': 'جاري التحديث...',
    'Batch updated successfully!': 'تم تحديث الدفعة بنجاح!',
    'Batch': 'دفعة',
    'Changing the batch will affect all users.': 'تغيير الدفعة سيؤثر على جميع المستخدمين.',
    'The ID of the Google Sheet containing batch data.': 'معرف جدول بيانات جوجل الذي يحتوي على بيانات الدفعة.',
    
    // Common
    loading: 'جاري التحميل...',
    error: 'خطأ',
    retry: 'إعادة المحاولة',
    close: 'إغلاق',
    save: 'حفظ',
    cancel: 'إلغاء',
    
    // Footer
    poweredBy: 'مدعوم من EduTransform',
    version: 'الإصدار 2.0'
  }
};

export const LanguageProvider = ({ children }) => {
  const [state, dispatch] = useReducer(languageReducer, initialState);

  useEffect(() => {
    // Load language from localStorage
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage && ['en', 'ar'].includes(savedLanguage)) {
      dispatch({ type: 'SET_LANGUAGE', payload: savedLanguage });
      dispatch({ type: 'SET_DIRECTION', payload: savedLanguage === 'ar' ? 'rtl' : 'ltr' });
    }
  }, []);

  useEffect(() => {
    // Update document direction and language
    document.documentElement.dir = state.direction;
    document.documentElement.lang = state.language;
    
    // Save to localStorage
    localStorage.setItem('language', state.language);
    
    // Update body class for styling
    document.body.classList.remove('rtl', 'ltr');
    document.body.classList.add(state.direction);
  }, [state.language, state.direction]);

  const setLanguage = (language) => {
    dispatch({ type: 'SET_LANGUAGE', payload: language });
    dispatch({ type: 'SET_DIRECTION', payload: language === 'ar' ? 'rtl' : 'ltr' });
  };

  const toggleLanguage = () => {
    dispatch({ type: 'TOGGLE_LANGUAGE' });
    dispatch({ type: 'SET_DIRECTION', payload: state.language === 'en' ? 'rtl' : 'ltr' });
  };

  const t = (key) => {
    return translations[state.language][key] || key;
  };

  const value = {
    ...state,
    setLanguage,
    toggleLanguage,
    t
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
